/* global google */
import React, { useMemo, useState } from "react";
import AddressAutocompleteInput from "./AddressAutocompleteInput";
import useShopStore from "../store/useShopStore";
import useUserAddressStore from "../store/useUserAddressStore";
import { loadGoogleMaps } from "../utils/loadGoogleMaps";

// Haversine in KM
function inRadius(user, center, radiusKm) {
  const R = 6371, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(user.lat - center.lat);
  const dLng = toRad(user.lng - center.lng);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(center.lat)) *
      Math.cos(toRad(user.lat)) *
      Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c <= Number(radiusKm || 0);
}

const GMAPS_KEY = process.env.REACT_APP_GMAPS_KEY;

export default function AddressGateModal({ isOpen, onClose, onPassed }) {
  const shop = useShopStore((s) => s.shop);

  // NEW: use the dedicated user-address store
  const setStructuredAddress = useUserAddressStore((s) => s.setStructuredAddress);
  const markDeliveryOk = useUserAddressStore((s) => s.markDeliveryOk);

  const [step, setStep] = useState("street"); // "street" | "number"
  const [street, setStreet] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [pickedStreetCenter, setPickedStreetCenter] = useState(null); // {lat,lng}
  const [pickedDisplay, setPickedDisplay] = useState("");
  const [error, setError] = useState("");

  // parse "lat,lng" string OR object
  const center = useMemo(() => {
    const raw = shop?.storeAddress?.coordinates;
    if (!raw) return { lat: NaN, lng: NaN };
    if (typeof raw === "string") {
      const [la, ln] = raw.split(",").map((s) => parseFloat(String(s).trim()));
      return { lat: la, lng: ln };
    }
    return { lat: Number(raw.lat), lng: Number(raw.lng) };
  }, [shop]);

  const radiusKm = useMemo(
    () => (shop?.orderOperateRadius != null ? Number(shop.orderOperateRadius) : NaN),
    [shop]
  );

  const shopReady =
    Number.isFinite(center.lat) &&
    Number.isFinite(center.lng) &&
    Number.isFinite(radiusKm) &&
    radiusKm > 0;

  if (!isOpen) return null;

  const handleStreetPicked = (p) => {
    setError("");
    setStreet(p.street || "");
    setCity(p.city || "");
    setPostalCode(p.postalCode || "");
    setPickedStreetCenter(p.lat && p.lng ? { lat: p.lat, lng: p.lng } : null);
    if (p.houseNumber) setHouseNumber(p.houseNumber); // prefill if Google provided
    setPickedDisplay(p.address || p.street || "");
  };

  const goNext = (e) => {
    e.preventDefault();
    setError("");
    if (!shopReady) return setError("Sklep nie ma skonfigurowanego obszaru dostawy.");
    if (!street.trim()) return setError("Wybierz ulicę z listy.");
    setStep("number");
  };

  const goBack = (e) => {
    e.preventDefault();
    setError("");
    setStep("street");
  };

  const confirmNumber = async (e) => {
    e.preventDefault();
    setError("");

    if (!shopReady) return setError("Sklep nie ma skonfigurowanego obszaru dostawy.");
    if (!street.trim()) {
      setStep("street");
      return setError("Najpierw wybierz ulicę.");
    }
    if (!houseNumber.trim()) return setError("Podaj numer budynku.");

    try {
      await loadGoogleMaps(GMAPS_KEY);

      // 1) Try GEOCODER with full context (street + number + city + postal)
      const fullTextRaw = `${street} ${houseNumber}, ${postalCode ? postalCode + " " : ""}${city || ""}, Poland`;
      const fullText = fullTextRaw
        .replace(/\s+,/g, ",")
        .replace(/\s{2,}/g, " ")
        .trim();

      const geocoder = new google.maps.Geocoder();
      let loc = null,
        formatted = null;

      const geo = await new Promise((resolve) =>
        geocoder.geocode({ address: fullText }, (res, status) =>
          resolve({ res, status })
        )
      );
      if (geo.status === "OK" && geo.res?.length) {
        loc = geo.res[0]?.geometry?.location || null;
        formatted = geo.res[0]?.formatted_address || null;
      }

      // 2) Fallback: Places AutocompleteService -> Places Details (biased near the picked street)
      if (!loc) {
        const svc = new google.maps.places.AutocompleteService();
        const predictions = await new Promise((resolve) =>
          svc.getPlacePredictions(
            {
              input: fullText,
              componentRestrictions: { country: "pl" },
              ...(pickedStreetCenter
                ? {
                    locationBias: {
                      center: new google.maps.LatLng(
                        pickedStreetCenter.lat,
                        pickedStreetCenter.lng
                      ),
                      radius: 2000,
                    },
                  }
                : {}),
            },
            (res) => resolve(res)
          )
        );

        if (predictions?.length) {
          const placeSvc = new google.maps.places.PlacesService(
            document.createElement("div")
          );
          const details = await new Promise((resolve) =>
            placeSvc.getDetails(
              {
                placeId: predictions[0].place_id,
                fields: ["geometry", "formatted_address"],
              },
              (res, status) => resolve({ res, status })
            )
          );
          if (details.status === "OK" && details.res?.geometry?.location) {
            loc = details.res.geometry.location;
            formatted = details.res.formatted_address;
          }
        }
      }

      // 3) Last resort: use street center (still fine for radius check)
      if (!loc && pickedStreetCenter) {
        loc = new google.maps.LatLng(
          pickedStreetCenter.lat,
          pickedStreetCenter.lng
        );
        formatted = `${street} ${houseNumber}, ${city || ""}`.trim();
      }

      if (!loc)
        return setError("Nie udało się odnaleźć adresu. Sprawdź numer budynku.");

      const user = { lat: loc.lat(), lng: loc.lng() };
      if (!inRadius(user, center, radiusKm))
        return setError("Adres poza obszarem dostawy.");

      const addrObj = {
        street,
        houseNumber,
        city,
        postalCode,
        full: formatted || `${street} ${houseNumber}, ${city || ""}`.trim(),
        short: `${street} ${houseNumber}`.trim(),
        coords: user,
      };

      // Save to the new dedicated store
      setStructuredAddress(addrObj);
      markDeliveryOk(true, shop?.id || null);

      // (Optional backward-compat: also update any legacy fields in useShopStore if you still rely on them elsewhere)
      try {
        const setLegacy = useShopStore.getState().setUserAddressData; // (full, short)
        if (typeof setLegacy === "function") setLegacy(addrObj.full, addrObj.short);
      } catch {}

      onClose?.();
      onPassed?.();
    } catch (err) {
      console.error(err);
      setError("Wystąpił błąd przy geokodowaniu adresu.");
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-container"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 480 }}
      >
        <button className="modal-close-button" onClick={onClose}>
          &times;
        </button>

        {step === "street" && (
          <form onSubmit={goNext} className="modal-content">
            <h3>Podaj ulicę</h3>
            <p className="muted">Gdzie mamy dostarczyć zamówienie?</p>

            <AddressAutocompleteInput onPlace={handleStreetPicked} />
            {pickedDisplay && (
              <div className="chip" style={{ marginTop: 8 }}>
                Wybrano: {pickedDisplay}
              </div>
            )}

            {!shopReady && (
              <div className="muted" style={{ marginTop: 8 }}>
                Ładuję konfigurację dostawy sklepu…
              </div>
            )}
            {error && <div className="location-modal-error">{error}</div>}

            <button className="confirm-add-button" type="submit" disabled={!shopReady}>
              Dalej
            </button>
          </form>
        )}

        {step === "number" && (
          <form onSubmit={confirmNumber} className="modal-content">
            <h3>Numer budynku</h3>
            <p className="muted">
              Ulica: <strong>{street}</strong>
              {city ? `, ${city}` : ""}
              {postalCode ? ` (${postalCode})` : ""}
            </p>

            <div style={{ display: "flex", gap: 8 }}>
              <input
                type="text"
                className="location-modal-input"
                placeholder="Np. 12A"
                value={houseNumber}
                onChange={(e) => setHouseNumber(e.target.value)}
              />
            </div>

            {error && <div className="location-modal-error">{error}</div>}

            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button className="modal-secondary-button" onClick={goBack}>
                Wstecz
              </button>
              <button className="confirm-add-button" type="submit">
                Sprawdź dostępność
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
